import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  CONFIRM_BOOKING_V2,
  DOWNLOAD_BOOKING_V2,
  DOWNLOAD_BOOKING_V2_EMPTY,
  GET_BOOKING_LIST_V2,
  GET_BOOKING_LIST_V2_EMPTY,
} from '../../../../../../store/actions';
import { ActionConfirmation, BreadCrumb, Loader } from '../../../../../Common';
import { Container } from 'reactstrap';
import Filter from './Components/Filters/Filter';
import Table from './Components/Tables/Table';
import { Constant } from '../../../../../Helpers/constant';
import UpdateBooking from './Components/Modals/UpdateBooking';

const MeetGreetBooking = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState(null);
  const [bookingType, setBookingType] = useState(['MEET_AND_GREET_BOOKING']);
  const [loading, setLoading] = useState(false);
  const [isShowConfirmationModal, setIsShowConfirmationModal] = useState(false);
  const [currentSelectedBookingDetails, setCurrentSelectedBookingDetails] = useState([]);
  const [updateType, setUpdateType] = useState('');
  const [isDetailsPage, setIsDetailsPage] = useState(false);

  /* ---------------------------- REDUX STATES ---------------------------- */
  // Bookings
  const { bookings, bookingsLoading, bookingsError } = useSelector((state: any) => ({
    bookings: state?.BookingsV2?.data,
    bookingsLoading: state?.BookingsV2?.loading,
    bookingsError: state?.BookingsV2?.error,
  }));

  // Confirm Booking
  const { confirmBooking, confirmBookingLoading } = useSelector((state: any) => ({
    confirmBooking: state?.ConfirmBookingV2?.data,
    confirmBookingLoading: state?.ConfirmBookingV2?.loading,
  }));

  // Cancel Booking
  const { cancelBooking, cancelBookingLoading } = useSelector((state: any) => ({
    cancelBooking: state?.CancelBookingV2?.data,
    cancelBookingLoading: state?.CancelBookingV2?.loading,
  }));

  // Click Download Dump
  const { downloadDump, downloadDumpLoading } = useSelector((state: any) => ({
    downloadDump: state?.DownloadDumpV2?.data,
    downloadDumpLoading: state?.DownloadDumpV2?.loading,
  }));

  /* ---------------------------- DISPATCHING ---------------------------- */
  useEffect(() => {
    return () => {
      dispatch({
        type: GET_BOOKING_LIST_V2_EMPTY,
      });
    };
  }, [dispatch]);

  useEffect(() => {
    if (downloadDump !== null) {
      dispatch({
        type: DOWNLOAD_BOOKING_V2_EMPTY,
      });
    }
  }, [downloadDump]);

  useEffect(() => {
    if (confirmBooking !== null || cancelBooking !== null) {
      setCurrentPageNumber(1);
      dispatch({
        type: GET_BOOKING_LIST_V2,
        payload: {
          data: {
            booking_type: bookingType,
            page: 1,
            sort_by: 'updated_at',
            page_size: Constant.TABLE_PAGE_SIZE,
            sort_direction: 'DESC',
          },
        },
      });
    }
  }, [confirmBooking, cancelBooking, dispatch, currentPageNumber]);

  useEffect(() => {
    dispatch({
      type: GET_BOOKING_LIST_V2,
      payload: {
        data: {
          booking_type: bookingType,
          page: currentPageNumber,
          sort_by: 'updated_at',
          page_size: Constant.TABLE_PAGE_SIZE,
          sort_direction: 'DESC',
        },
      },
    });
  }, [dispatch]);

  /* ---------------------------- BREADCRUMB HANDLER ---------------------------- */
  const breadcrumNavigationHandler = title => {
    if (title === `Dashboard`) {
      history.push(`/dashboard`);
    } else if (title === `Dynamic Coupon Booking`) {
      // do nothing
    }
  };

  /* ---------------------------- FILTER SUBMIT HANDLER ---------------------------- */
  const filterSubmitHandler = filters => {
    if (filters?.filter) {
      setCurrentPageNumber(1);
      setCurrentlyAppliedFilters(filters);

      dispatch({
        type: GET_BOOKING_LIST_V2,
        payload: {
          data: {
            booking_type: bookingType,
            page: 1,
            page_size: Constant.TABLE_PAGE_SIZE,
            sort_direction: 'DESC',
            ...filters,
          },
        },
      });
    } else {
      setCurrentlyAppliedFilters(filters);
    }
  };

  /* ---------------------------- FILTER RESET HANDLER ---------------------------- */
  const onResetHandler = () => {
    setCurrentPageNumber(1);

    dispatch({
      type: GET_BOOKING_LIST_V2,
      payload: {
        data: {
          booking_type: bookingType,
          page: 1,
          page_size: Constant.TABLE_PAGE_SIZE,
          sort_direction: 'DESC',
        },
      },
    });
  };

  /* ---------------------------- PAGINATION HANDLER ---------------------------- */
  const paginationHandler = pageNumber => {
    setCurrentPageNumber(pageNumber + 1);

    dispatch({
      type: GET_BOOKING_LIST_V2,
      payload: {
        data: {
          booking_type: bookingType,
          page: pageNumber + 1,
          page_size: Constant.TABLE_PAGE_SIZE,
          sort_by: currentlyAppliedFilters?.sort_by ?? 'updated_at',
          sort_direction: 'DESC',
          ...currentlyAppliedFilters,
        },
      },
    });
  };

  /* ---------------------------- DOWNLOAD DUMP HANDLER ---------------------------- */
  const downloadDumpHandler = () => {
    dispatch({
      type: DOWNLOAD_BOOKING_V2,
      payload: {
        data: {
          booking_type: bookingType,
          ...currentlyAppliedFilters,
        },
      },
    });
  };

  useEffect(() => {
    if (bookingsLoading || downloadDumpLoading || confirmBookingLoading || cancelBookingLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [bookingsLoading, downloadDumpLoading, confirmBookingLoading, cancelBookingLoading]);

  /* ---------------------------- TABLE ACTION HANDLER ---------------------------- */
  const tableActionHandler = params => {
    setCurrentSelectedBookingDetails(params?.bookingDetails);
    if (params?.actionType === `VIEW`) {
      setIsDetailsPage(!isDetailsPage);
    } else if (params?.actionType === `CONFIRM`) {
      setIsShowConfirmationModal(true);
      setUpdateType('Confirm');
    } else if (params?.actionType === `CANCEL`) {
      setIsShowConfirmationModal(true);
      setUpdateType('Cancel');
    }
  };

  return (
    <>
      {loading && <Loader />}

      {isShowConfirmationModal && (
        <UpdateBooking onCloseHandler={() => setIsShowConfirmationModal(false)} data={currentSelectedBookingDetails} updateType={updateType} />
      )}

      <div className="page-content">
        <Container fluid className="position-relative">
          {/*  ----------------------------  UI'S ---------------------------- */}

          <>
            <BreadCrumb
              title="Delayed Coupon Booking"
              navigationby={breadcrumNavigationHandler}
              navigation={[`Dashboard`, `Dynamic Coupon Booking`]}
            />
            <Filter data={{}} filterHandler={filterSubmitHandler} appliedFilters={currentlyAppliedFilters} onReset={onResetHandler} />

            <Table
              data={bookings?.result}
              totalData={bookings?.total_count}
              pageNumber={currentPageNumber}
              pageHandler={paginationHandler}
              actionHandler={tableActionHandler}
              appliedFilters={currentlyAppliedFilters}
              dumpDownload={downloadDumpHandler}
            />
          </>
        </Container>
      </div>
    </>
  );
};

export default MeetGreetBooking;
